import React, { useContext, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";

import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import CalculationTabs from "../../../components/CalculationTabs";
import { getRop } from "../../../api/ropApi";
import { LanguageContext } from "../../../components/LanguageContext";

const AnalysisPage = () => {
    const { excelId } = useParams();
    const { translations } = useContext(LanguageContext) || {};

    const { data, isLoading } = useQuery({
        queryKey: ["ropGet", excelId],
        queryFn: () => getRop(excelId),
    });

    const columns = useMemo(
        () => [
            {
                field: "rop_id",
                headerName: "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "supplier_name",
                headerName:
                    translations?.poslovodjaProizvodiSupplierName || "Dobavljač",
                minWidth: 180,
                flex: 1,
            },
            {
                field: "code",
                headerName:
                    translations?.adminAnalysisProductCode || "Šifra proizvoda",
                minWidth: 140,
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "name",
                headerName:
                    translations?.adminAnalysisProductName || "Naziv proizvoda",
                minWidth: 240,
                flex: 2,
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={params.value}
                    >
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: "safety_storage",
                headerName:
                    translations?.adminAnalysisSafetyStock || "Sigurnosna zaliha",
                minWidth: 140,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    >
                        {parseFloat(params.row.safety_storage).toFixed(2)}
                    </Typography>
                ),
            },
            {
                field: "rop",
                headerName: translations?.adminAnalysisROP || "ROP",
                minWidth: 120,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            color: "primary.main",
                        }}
                    >
                        {parseFloat(params.row.rop).toFixed(2)}
                    </Typography>
                ),
            },
        ],
        [translations]
    );

    const rows = useMemo(() => data?.data || [], [data]);

    return (
        <Box>
            <PageHeader
                icon={<LeaderboardOutlinedIcon />}
                eyebrow={
                    translations?.adminExcelProInfoPageAnalysis || "Analiza"
                }
                title={
                    translations?.adminAnalysisPageTitle || "Analiza i sigurnosne zalihe"
                }
                description={
                    translations?.adminAnalysisPageSubtitle ||
                    "ROP i sigurnosne zalihe po proizvodu."
                }
            />

            <CalculationTabs active="analiza" />

            <SectionCard padded={false}>
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    rowHeight={56}
                    emptyIcon={<LeaderboardOutlinedIcon />}
                    emptyTitle={
                        translations?.messageIfEmptyData ||
                        "Nema podataka za prikaz"
                    }
                />
            </SectionCard>
        </Box>
    );
};

export default AnalysisPage;